import { keepPreviousData, useQuery, type UseQueryOptions } from '@tanstack/vue-query';
import type { Ref } from 'vue';
import { store } from '@/plugins/store';
import { ActionType } from '@/plugins/store/actions';
import type { MortalityHistoryResponse, MortalityObjectivesResponse } from '@/types/MortalityObjective';

const DEFAULT_OPTIONS = {
    staleTime: 1000 * 60, // 1 minute
    gcTime: 1000 * 60 * 5, // 5 minutes
};

export const queryKeys = {
    getMortalityObjectivesOverviewRoot: ['objectives', 'mortality-overview'],
    // `isWeaners`/`siteIds` aren't sent as explicit params (the dispatched action reads them off
    // store state/getters itself) but they do change the response, so they still belong in the key
    // — otherwise switching the global pig-type or farm/site selection wouldn't invalidate the
    // cached page.
    getMortalityObjectivesOverview: (weeks: Ref<number>, isWeaners: Ref<boolean>, siteIds: Ref<number[]>) => [...queryKeys.getMortalityObjectivesOverviewRoot, weeks, isWeaners, siteIds],

    getMortalityObjectiveHistoryRoot: ['objectives', 'mortality-history'],
    getMortalityObjectiveHistory: (weeks: Ref<number>, periods: Ref<number>, isWeaners: Ref<boolean>, siteIds: Ref<number[]>) => [
        ...queryKeys.getMortalityObjectiveHistoryRoot,
        weeks,
        periods,
        isWeaners,
        siteIds,
    ],
};

// Farm → Barn [→ Section] Objectives overview, addressed by the reference-window (weeks)
// toggle. Wraps the GetMortalityObjectives action (which takes `weeks` as an explicit
// form param) rather than calling ApiClient directly, mirroring useBarnProductionGroups — the query hook
// is the reactive/caching seam, the Vuex action stays the one place that knows the request shape.
export function useMortalityObjectivesOverview(
    { weeks, isWeaners, siteIds }: { weeks: Ref<number>; isWeaners: Ref<boolean>; siteIds: Ref<number[]> },
    options: UseQueryOptions<MortalityObjectivesResponse> = {}
) {
    return useQuery<MortalityObjectivesResponse>({
        queryKey: queryKeys.getMortalityObjectivesOverview(weeks, isWeaners, siteIds),
        queryFn: () =>
            store.dispatch(ActionType.GetMortalityObjectives, {
                form: { weeks: weeks.value },
                options: { cacheResponse: false, ignoreCache: true },
            }),
        placeholderData: keepPreviousData,
        ...DEFAULT_OPTIONS,
        ...options,
    });
}

// Farm-level trend history (sparkline) — same reactive-key shape as
// useMortalityObjectivesOverview above, plus `periods` (how many consecutive, non-overlapping
// buckets to fetch; the sparkline tile passes a fixed default, no UI toggle for this yet).
export function useMortalityObjectiveHistory(
    { weeks, periods, isWeaners, siteIds }: { weeks: Ref<number>; periods: Ref<number>; isWeaners: Ref<boolean>; siteIds: Ref<number[]> },
    options: UseQueryOptions<MortalityHistoryResponse> = {}
) {
    return useQuery<MortalityHistoryResponse>({
        queryKey: queryKeys.getMortalityObjectiveHistory(weeks, periods, isWeaners, siteIds),
        queryFn: () =>
            store.dispatch(ActionType.GetMortalityObjectiveHistory, {
                form: { weeks: weeks.value, periods: periods.value },
                options: { cacheResponse: false, ignoreCache: true },
            }),
        placeholderData: keepPreviousData,
        ...DEFAULT_OPTIONS,
        ...options,
    });
}
